import React from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useTheme } from 'contexts/ThemeContext';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'danger' | 'warning';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText = 'Cancel',
  confirmColor = 'primary',
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  if (!isOpen) return null;

  const getIcon = () => {
    switch (confirmColor) {
      case 'danger':
        return {
          icon: 'bi-exclamation-triangle-fill',
          color: isDark ? '#FCA5A5' : '#DC2626',
          bg: isDark ? '#2D1B1B' : '#FEE2E2',
        };
      case 'warning':
        return {
          icon: 'bi-exclamation-circle-fill',
          color: isDark ? '#FCD34D' : '#D97706',
          bg: isDark ? '#2D2305' : '#FEF3C7',
        };
      default:
        return {
          icon: 'bi-info-circle-fill',
          color: isDark ? '#93C5FD' : '#2563EB',
          bg: isDark ? '#1E293B' : '#DBEAFE',
        };
    }
  };

  const iconConfig = getIcon();

  // Dark mode color tokens
  const modalBg = isDark ? '#1E293B' : '#FFFFFF';
  const headerBorder = isDark ? '#314158' : '#E5E7EB';
  const titleColor = isDark ? '#E2E8F0' : '#111827';
  const messageColor = isDark ? '#90A1B9' : '#4B5563';
  const footerBg = isDark ? '#0F172B' : '#F9FAFB';
  const cancelBg = isDark ? '#1E293B' : '#FFFFFF';
  const cancelBorder = isDark ? '#314158' : '#D1D5DB';
  const cancelText_color = isDark ? '#E2E8F0' : '#374151';
  const cancelHoverBg = isDark ? '#314158' : '#F9FAFB';
  const closeIconColor = isDark ? '#90A1B9' : '#9CA3AF';

  const getDetailsSection = () => {
    if (confirmColor === 'danger') {
      const detailBg = isDark ? 'rgba(220, 38, 38, 0.1)' : '#FEF2F2';
      const detailBorder = isDark ? '#7F1D1D' : '#FECACA';
      const detailTitle = isDark ? '#FCA5A5' : '#991B1B';
      const detailText = isDark ? '#FCA5A5' : '#B91C1C';
      return (
        <div
          className="mt-3 p-3 rounded-lg"
          style={{ background: detailBg, border: `1px solid ${detailBorder}` }}
        >
          <p className="text-xs font-semibold mb-2" style={{ color: detailTitle }}>
            {t('modals.confirmation.danger.warning')}
          </p>
          <ul className="space-y-1 text-xs" style={{ color: detailText }}>
            <li className="flex items-start gap-2">
              <i className="bi bi-x-circle-fill mt-0.5 flex-shrink-0"></i>
              <span>{t('modals.confirmation.danger.rulesDeleted')}</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="bi bi-x-circle-fill mt-0.5 flex-shrink-0"></i>
              <span>{t('modals.confirmation.danger.dataLost')}</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="bi bi-x-circle-fill mt-0.5 flex-shrink-0"></i>
              <span>{t('modals.confirmation.danger.cannotUndo')}</span>
            </li>
          </ul>
        </div>
      );
    } else if (confirmColor === 'warning') {
      const detailBg = isDark ? 'rgba(245, 158, 11, 0.1)' : '#FFFBEB';
      const detailBorder = isDark ? '#92400E' : '#FDE68A';
      const detailTitle = isDark ? '#FCD34D' : '#92400E';
      const detailText = isDark ? '#FCD34D' : '#A16207';
      return (
        <div
          className="mt-3 p-3 rounded-lg"
          style={{ background: detailBg, border: `1px solid ${detailBorder}` }}
        >
          <p className="text-xs font-semibold mb-2" style={{ color: detailTitle }}>
            {t('modals.confirmation.warning.header')}
          </p>
          <ul className="space-y-1 text-xs" style={{ color: detailText }}>
            <li className="flex items-start gap-2">
              <i className="bi bi-pause-circle-fill mt-0.5 flex-shrink-0"></i>
              <span>{t('modals.confirmation.warning.featuresPause')}</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="bi bi-shield-check mt-0.5 flex-shrink-0"></i>
              <span>{t('modals.confirmation.warning.settingsPreserved')}</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="bi bi-arrow-clockwise mt-0.5 flex-shrink-0"></i>
              <span>{t('modals.confirmation.warning.reEnableAnytime')}</span>
            </li>
          </ul>
        </div>
      );
    } else {
      const detailBg = isDark ? 'rgba(59, 130, 246, 0.1)' : '#EFF6FF';
      const detailBorder = isDark ? '#1E40AF' : '#BFDBFE';
      const detailTitle = isDark ? '#93C5FD' : '#1E40AF';
      const detailText = isDark ? '#93C5FD' : '#1D4ED8';
      return (
        <div
          className="mt-3 p-3 rounded-lg"
          style={{ background: detailBg, border: `1px solid ${detailBorder}` }}
        >
          <p className="text-xs font-semibold mb-2" style={{ color: detailTitle }}>
            {t('modals.confirmation.primary.header')}
          </p>
          <ul className="space-y-1 text-xs" style={{ color: detailText }}>
            <li className="flex items-start gap-2">
              <i className="bi bi-play-circle-fill mt-0.5 flex-shrink-0"></i>
              <span>{t('modals.confirmation.primary.featuresResume')}</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="bi bi-gear-fill mt-0.5 flex-shrink-0"></i>
              <span>{t('modals.confirmation.primary.settingsApplied')}</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="bi bi-graph-up mt-0.5 flex-shrink-0"></i>
              <span>{t('modals.confirmation.primary.growthContinue')}</span>
            </li>
          </ul>
        </div>
      );
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
      <div className="rounded-xl w-full max-w-md shadow-2xl" style={{ background: modalBg }}>
        {/* Header with Icon */}
        <div className="p-4" style={{ borderBottom: `1px solid ${headerBorder}` }}>
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: iconConfig.bg }}
            >
              <i className={`bi ${iconConfig.icon} text-lg`} style={{ color: iconConfig.color }}></i>
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold" style={{ color: titleColor }}>{title}</h3>
              <p className="text-xs mt-1" style={{ color: messageColor }}>{message}</p>
            </div>
            <button
              onClick={onClose}
              className="transition-colors hover:opacity-80"
              style={{ color: closeIconColor }}
            >
              <i className="bi bi-x-lg text-lg" />
            </button>
          </div>
        </div>

        {/* Body with Details */}
        <div className="p-4">
          {getDetailsSection()}
        </div>

        {/* Footer with Actions */}
        <div
          className="p-4 rounded-b-xl flex justify-end gap-2"
          style={{ background: footerBg }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg transition-colors text-xs font-medium"
            style={{
              background: cancelBg,
              border: `1px solid ${cancelBorder}`,
              color: cancelText_color,
            }}
            onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.background = cancelHoverBg; }}
            onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.background = cancelBg; }}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg text-xs font-medium text-white transition-colors flex items-center gap-2 ${
              confirmColor === 'primary'
                ? 'bg-blue-600 hover:bg-blue-700'
                : confirmColor === 'danger'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-yellow-600 hover:bg-yellow-700'
            }`}
          >
            {confirmColor === 'danger' && <i className="bi bi-trash-fill"></i>}
            {confirmColor === 'warning' && <i className="bi bi-pause-fill"></i>}
            {confirmColor === 'primary' && <i className="bi bi-play-fill"></i>}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ConfirmationModal;
